import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'customer-success.desk.pane-collapse';

export const DESK_COLLAPSED_PANE_SIZE = '2.75rem';
export const DESK_QUEUES_PANE_SIZE = '16rem';
export const DESK_CLASSIFICATION_PANE_SIZE = 'minmax(16rem, 18rem)';

type SupportDeskPaneCollapseState = {
  queues: boolean;
  tickets: boolean;
  classification: boolean;
};

const DEFAULT_STATE: SupportDeskPaneCollapseState = {
  queues: false,
  tickets: false,
  classification: false,
};

function readState(): SupportDeskPaneCollapseState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<SupportDeskPaneCollapseState>;
    return {
      queues: Boolean(parsed.queues),
      tickets: Boolean(parsed.tickets),
      classification: Boolean(parsed.classification),
    };
  } catch {
    return DEFAULT_STATE;
  }
}

export function useSupportDeskPaneCollapse() {
  const [state, setState] = useState<SupportDeskPaneCollapseState>(readState);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore quota / private mode
    }
  }, [state]);

  const toggleQueues = useCallback(() => {
    setState((current) => ({ ...current, queues: !current.queues }));
  }, []);

  const toggleTickets = useCallback(() => {
    setState((current) => ({ ...current, tickets: !current.tickets }));
  }, []);

  const toggleClassification = useCallback(() => {
    setState((current) => ({ ...current, classification: !current.classification }));
  }, []);

  return {
    queuesCollapsed: state.queues,
    ticketsCollapsed: state.tickets,
    classificationCollapsed: state.classification,
    toggleQueues,
    toggleTickets,
    toggleClassification,
  };
}
